import { useSelector } from 'src/hooks/useRedux'
import {
  getSportById,
  getGenericSportById,
} from '@sportninja/common/selectors/types'
import {
  getSportTerminology,
  SportTerminology,
} from '@sportninja/common/utils/games/sportTerminology'

/**
 * Minimal shape of the game fields this hook reads.
 * Accepts any object that carries sport identity — avoids coupling
 * to the full `Game` interface from `useGame.ts`.
 */
interface GameSportFields {
  sport_id?: string | null
  generic_sport_id?: string | null
  generic_sport_name?: string | null
}

interface UseGameSportParams {
  game: GameSportFields | null | undefined
}

export interface UseGameSportResult {
  /** Full sport record from Redux `state.types.sports` — null if not loaded / unknown */
  sport: any | null
  /** Full generic-sport record when the game is a generic sport — null otherwise */
  genericSport: any | null
  /** Raw sport id off the game */
  sportId: string | null
  /** Canonical sport name: 'hockey' | 'soccer' | 'generic' | null */
  sportName: string | null
  /** Free-text generic sport name from the game (e.g. "basketball") */
  genericSportName: string | null
  isHockey: boolean
  isSoccer: boolean
  /** True when the game is tied to a generic sport (basketball, lacrosse, etc.) */
  isGeneric: boolean
  /** Display labels to use for this sport — always populated, never null */
  terminology: SportTerminology
}

/**
 * Central web hook for resolving a game's sport identity and display terminology.
 *
 * Web counterpart to `mobile/src/hooks/useGameSport.ts`. Uses the bespoke
 * `useSelector` from `web/src/hooks/useRedux` because web is pinned to
 * `react-redux@5` which predates hooks. Otherwise identical to the mobile
 * hook and shares the same pure `getSportTerminology` helper from common.
 *
 * Reads from Redux `state.types.sports`, which is loaded once at app boot.
 */
const useGameSport = ({ game }: UseGameSportParams): UseGameSportResult => {
  const sport = useSelector((state: any) =>
    game?.sport_id ? getSportById(state, game.sport_id) : null
  )
  const genericSport = useSelector((state: any) =>
    game?.generic_sport_id
      ? getGenericSportById(state, game.generic_sport_id)
      : null
  )

  const sportName: string | null = sport?.name ?? null
  const isHockey = sportName === 'hockey'
  const isSoccer = sportName === 'soccer'
  const isGeneric =
    game?.generic_sport_id !== null && game?.generic_sport_id !== undefined

  const genericSportName =
    game?.generic_sport_name ?? genericSport?.name ?? null

  const terminology = getSportTerminology({
    sportName: isGeneric ? 'generic' : sportName,
    genericSportName,
  })

  return {
    sport: sport || null,
    genericSport,
    sportId: game?.sport_id ?? null,
    sportName,
    genericSportName,
    isHockey,
    isSoccer,
    isGeneric,
    terminology,
  }
}

export default useGameSport
